<template>
  <v-app :class="getAppClass()">
    <!-- Navigation drawer pour les espaces authentifiés (HR/Admin) -->
    <v-navigation-drawer v-if="showNavigation" v-model="drawer" :rail="rail" permanent @click="rail = false">
      <v-list-item prepend-avatar="/logo.png" :title="rail ? '' : 'Recruitment App'" nav>
        <template v-slot:append>
          <v-btn variant="text" icon="mdi-chevron-left" @click.stop="rail = !rail"></v-btn>
        </template>
      </v-list-item>

      <v-divider></v-divider>

      <v-list density="compact" nav>
        <!-- Navigation RH -->
        <template v-if="userStore.hasRole('hr')">
          <v-list-item prepend-icon="mdi-view-dashboard" title="Dashboard" to="/hr/dashboard"
            value="hr-dashboard"></v-list-item>
          <v-list-item prepend-icon="mdi-file-document-outline" title="Fiches de besoin" to="/hr/needs"
            value="hr-needs"></v-list-item>
          <v-list-item prepend-icon="mdi-briefcase-outline" title="Fiches de poste" to="/hr/jobs"
            value="hr-jobs"></v-list-item>
          <v-list-item prepend-icon="mdi-account-group" title="Candidats" to="/hr/candidates"
            value="hr-candidates"></v-list-item>
        </template>

        <!-- Navigation Candidat -->
        <template v-if="userStore.hasRole('candidate')">
          <v-list-item prepend-icon="mdi-account-check" title="Statut candidature" to="/status"
            value="candidate-status"></v-list-item>
          <v-list-item prepend-icon="mdi-file-send" title="Mes candidatures" to="/applications"
            value="candidate-applications"></v-list-item>
          <v-list-item prepend-icon="mdi-help-circle" title="Questionnaires" to="/questionnaires"
            value="candidate-questionnaires"></v-list-item>
        </template>

        <!-- Navigation Admin -->
        <template v-if="userStore.hasRole('admin')">
          <v-list-item prepend-icon="mdi-shield-crown" title="Administration" to="/admin/dashboard"
            value="admin-dashboard"></v-list-item>
          <v-list-item prepend-icon="mdi-robot" title="Paramètres LLM" to="/admin/llm" value="admin-llm"></v-list-item>
          <v-list-item prepend-icon="mdi-database" title="Base de données" to="/admin/database"
            value="admin-database"></v-list-item>
          <v-list-item prepend-icon="mdi-broadcast" title="Canaux publication" to="/admin/channels"
            value="admin-channels"></v-list-item>
        </template>
      </v-list>

      <template v-slot:append>
        <v-list>
          <v-list-item prepend-icon="mdi-logout" title="Déconnexion" @click="logout"></v-list-item>
        </v-list>
      </template>
    </v-navigation-drawer>

    <!-- App bar seulement pour les espaces authentifiés -->
    <v-app-bar v-if="showNavigation && userStore.isAuthenticated" :order="-1" scroll-behavior="hide">
      <v-app-bar-nav-icon variant="text" @click.stop="drawer = !drawer"></v-app-bar-nav-icon>
      <v-toolbar-title>{{ getPageTitle() }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon="mdi-theme-light-dark" @click="toggleTheme"></v-btn>
      <v-btn icon="mdi-bell" @click="showNotifications = true">
        <v-badge v-if="notifications.length > 0" color="error" :content="notifications.length">
          <v-icon>mdi-bell</v-icon>
        </v-badge>
        <v-icon v-else>mdi-bell</v-icon>
      </v-btn>
      <v-menu>
        <template v-slot:activator="{ props }">
          <v-btn v-bind="props" icon>
            <v-avatar size="32">
              <v-img v-if="userStore.user?.avatar" :src="userStore.user.avatar" :alt="userStore.user.name"></v-img>
              <v-icon v-else>mdi-account-circle</v-icon>
            </v-avatar>
          </v-btn>
        </template>
        <v-list>
          <v-list-item>
            <v-list-item-title>{{ userStore.user?.name || 'Utilisateur' }}</v-list-item-title>
            <v-list-item-subtitle>{{ userStore.user?.email }}</v-list-item-subtitle>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item prepend-icon="mdi-account-cog" title="Profil" @click="goToProfile"></v-list-item>
          <v-list-item prepend-icon="mdi-logout" title="Déconnexion" @click="logout"></v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <v-main>
      <router-view />
    </v-main>

    <!-- Snackbar global pour les notifications -->
    <v-snackbar v-model="snackbarStore.show" :color="snackbarStore.color" :timeout="snackbarStore.timeout"
      location="top right" variant="elevated">
      <div class="d-flex align-center">
        <v-icon :icon="getSnackbarIcon(snackbarStore.type)" class="mr-2"></v-icon>
        {{ snackbarStore.message }}
      </div>
      <template #actions>
        <v-btn variant="text" @click="snackbarStore.hide()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Dialog notifications pour l'espace HR uniquement -->
    <v-dialog v-if="appMode === 'hr'" v-model="showNotifications" max-width="500">
      <v-card>
        <v-card-title>
          <span class="text-h6">Notifications</span>
        </v-card-title>
        <v-card-text>
          <v-list v-if="notifications.length > 0">
            <v-list-item v-for="notification in notifications" :key="notification.id"
              :prepend-icon="getNotificationIcon(notification.type)" :title="notification.title"
              :subtitle="notification.message">
              <template v-slot:append>
                <v-btn size="small" variant="text" icon="mdi-close"
                  @click="removeNotification(notification.id)"></v-btn>
              </template>
            </v-list-item>
          </v-list>
          <div v-else class="text-center py-4">
            <v-icon size="48" color="grey">mdi-bell-off</v-icon>
            <p class="text-grey mt-2">Aucune notification</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn v-if="notifications.length > 0" color="primary" variant="text" @click="clearAllNotifications">
            Tout supprimer
          </v-btn>
          <v-btn color="primary" variant="text" @click="showNotifications = false">
            Fermer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup lang="ts">
import { useSnackbarStore } from '@/stores/snackbar'
import { useUserStore } from '@/stores/user'
import { computed, inject, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useTheme } from 'vuetify'

const router = useRouter()
const theme = useTheme()
const userStore = useUserStore()
const snackbarStore = useSnackbarStore()
const appMode = inject<'candidate' | 'hr' | 'welcome'>('appMode', 'welcome')

const drawer = ref(true)
const rail = ref(false)
const showNotifications = ref(false)

// Afficher la navigation pour les espaces HR et candidat authentifié
const showNavigation = computed(() =>
  appMode === 'hr' || (appMode === 'candidate' && userStore.isAuthenticated)
)

const notifications = ref([
  {
    id: 1,
    type: 'info',
    title: 'Nouvelle candidature',
    message: 'Une nouvelle candidature a été reçue pour le poste de Développeur Frontend'
  },
  {
    id: 2,
    type: 'warning',
    title: 'Entretien programmé',
    message: 'N\'oubliez pas votre entretien demain à 14h00'
  }
])

const getAppClass = () => {
  return {
    'app-candidate': appMode === 'candidate',
    'app-hr': appMode === 'hr',
    'app-welcome': appMode === 'welcome'
  }
}

const getPageTitle = () => {
  const routeTitle = router.currentRoute.value.meta?.title
  if (routeTitle) return routeTitle

  const routeName = router.currentRoute.value.name as string
  const titleMap: Record<string, string> = {
    'hr-dashboard': 'Dashboard RH',
    'hr-needs': 'Gestion des besoins',
    'hr-jobs': 'Gestion des postes',
    'hr-candidates': 'Gestion des candidats',
    'candidate-status': 'Statut candidature',
    'candidate-applications': 'Mes candidatures',
    'candidate-questionnaires': 'Questionnaires',
    'admin-dashboard': 'Administration',
    'admin-llm': 'Paramètres LLM',
    'admin-database': 'Base de données',
    'admin-channels': 'Canaux de publication'
  }

  return titleMap[routeName] || 'Recruitment App'
}

const getSnackbarIcon = (type: string) => {
  const icons = {
    success: 'mdi-check-circle',
    error: 'mdi-alert-circle',
    warning: 'mdi-alert',
    info: 'mdi-information'
  }
  return icons[type as keyof typeof icons] || 'mdi-information'
}

const toggleTheme = () => {
  theme.global.name.value = theme.global.current.value.dark ? 'light' : 'dark'
}

const logout = async () => {
  try {
    await userStore.logout()
    router.push('/')
    snackbarStore.success('Déconnexion réussie')
  } catch (error) {
    console.error('Erreur lors de la déconnexion:', error)
    snackbarStore.error('Erreur lors de la déconnexion')
  }
}

const goToProfile = () => {
  router.push('/profile')
}

const getNotificationIcon = (type: string) => {
  const iconMap: Record<string, string> = {
    info: 'mdi-information',
    success: 'mdi-check-circle',
    warning: 'mdi-alert',
    error: 'mdi-alert-circle'
  }
  return iconMap[type] || 'mdi-information'
}

const removeNotification = (id: number) => {
  const index = notifications.value.findIndex(n => n.id === id)
  if (index > -1) {
    notifications.value.splice(index, 1)
  }
}

const clearAllNotifications = () => {
  notifications.value = []
}

onMounted(() => {
  // Vérifier l'authentification au démarrage
  if (!userStore.isAuthenticated && router.currentRoute.value.path !== '/login') {
    router.push('/login')
  }
})
</script>

<style scoped>
.v-navigation-drawer {
  transition: width 0.3s ease;
}
</style>
